import { useState, useRef, useEffect } from 'react'
import { formatRecordingTime } from '../utils/formatters'

/* SF Symbol: play.fill inside white circle */
const PlayFillInCircle = ({ size = 40 }) => (
  <div style={{
    width: `${size}px`, height: `${size}px`, borderRadius: '50%',
    background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  }}>
    <svg width={size * 0.4} height={size * 0.44} viewBox="0 0 8 11" fill="none">
      <path d="M0 9.22852V0.955078C0 0.310547 0.380859 0 0.832031 0C1.03125 0 1.23633 0.0585938 1.43555 0.169922L8.35547 4.21289C8.85938 4.50586 9.06445 4.72852 9.06445 5.0918C9.06445 5.44922 8.85938 5.67773 8.35547 5.9707L1.43555 10.0078C1.23633 10.1191 1.03125 10.1777 0.832031 10.1777C0.380859 10.1777 0 9.86719 0 9.22852Z" fill="var(--color-primary)"/>
    </svg>
  </div>
)

/* Stop icon inside white circle — darker blue circle with blue rounded square */
const PauseInCircle = ({ size = 40 }) => (
  <div style={{
    width: `${size}px`, height: `${size}px`, borderRadius: '50%',
    background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  }}>
      <div style={{
        width: `${size * 0.35}px`, height: `${size * 0.35}px`,
        borderRadius: `${size * 0.08}px`,
        background: 'var(--color-primary)',
      }} />
    </div>
)

/* Generate static waveform pattern — 60 bars with varied heights */
function generateWaveformBars(count = 60) {
  const bars = []
  for (let i = 0; i < count; i++) {
    // Create natural-looking variation with some peaks and valleys
    const base = Math.sin(i * 0.4) * 0.3 + Math.sin(i * 0.17) * 0.2 + 0.5
    const noise = (Math.sin(i * 2.3) + 1) * 0.15
    bars.push(Math.max(0.2, Math.min(1, base + noise)))
  }
  return bars
}

function VoiceMemoPlayer({ url, duration }) {
  const [playing, setPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [audioDuration, setAudioDuration] = useState(duration || 0)
  const audioRef = useRef(null)
  const [waveformBars] = useState(() => generateWaveformBars(120))

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const onTime = () => {
      setCurrentTime(audio.currentTime)
      if (audio.duration) setProgress(audio.currentTime / audio.duration)
    }
    const onMeta = () => {
      if (audio.duration && isFinite(audio.duration)) setAudioDuration(audio.duration)
    }
    const onEnd = () => { setPlaying(false); setProgress(0); setCurrentTime(0) }

    audio.addEventListener('timeupdate', onTime)
    audio.addEventListener('loadedmetadata', onMeta)
    audio.addEventListener('ended', onEnd)
    return () => {
      audio.removeEventListener('timeupdate', onTime)
      audio.removeEventListener('loadedmetadata', onMeta)
      audio.removeEventListener('ended', onEnd)
    }
  }, [url])

  const togglePlay = () => {
    if (!audioRef.current) return
    if (playing) {
      audioRef.current.pause()
      setPlaying(false)
    } else {
      audioRef.current.play()
      setPlaying(true)
    }
  }

  const handleSeek = (e) => {
    if (!audioRef.current || !audioDuration) return
    const rect = e.currentTarget.getBoundingClientRect()
    const clickX = e.clientX - rect.left
    const ratio = clickX / rect.width
    audioRef.current.currentTime = ratio * audioDuration
    setProgress(ratio)
  }

  const displayTime = currentTime > 0 ? currentTime : audioDuration

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '10px',
      background: 'var(--color-primary)',
      borderRadius: '100px',
      padding: '6px 16px 6px 6px',
      minHeight: '52px',
      width: '100%',
      marginTop: '8px',
    }}>
      <audio ref={audioRef} src={url} preload="metadata" style={{ display: 'none' }} />

      {/* Play/Pause button */}
      <button onClick={togglePlay} style={{
        background: 'none', border: 'none', cursor: 'pointer', padding: 0, flexShrink: 0,
      }}>
        {playing ? <PauseInCircle size={40} /> : <PlayFillInCircle size={40} />}
      </button>

      {/* Waveform — clickable to seek */}
      <div
        onClick={handleSeek}
        style={{
          flex: 1, display: 'flex', alignItems: 'center', gap: '1.5px',
          height: '32px', overflow: 'hidden', cursor: 'pointer', minWidth: 0,
        }}
      >
        {waveformBars.map((h, i) => {
          const playedThreshold = Math.floor(waveformBars.length * progress)
          const isPlayed = i < playedThreshold
          return (
            <div key={i} style={{
              flex: 1,
              height: `${Math.max(3, h * 28)}px`,
              borderRadius: '100px',
              background: isPlayed ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.5)',
              maxWidth: '2px',
              transition: 'background 0.1s linear',
            }} />
          )
        })}
      </div>

      {/* Duration */}
      <span style={{
        color: '#a3cfff', fontSize: '13px',
        fontVariantNumeric: 'tabular-nums', flexShrink: 0,
        fontWeight: '500', minWidth: '32px', textAlign: 'right',
      }}>
        {formatRecordingTime(Math.floor(displayTime))}
      </span>
    </div>
  )
}

export default VoiceMemoPlayer